import { useState, useCallback, useEffect } from 'react'
import { useToast } from '../../context/ToastContext'
import { InfoTip } from '../../components/InfoTip'
import { getLotSize, getQty } from '../../utils/lotSize'
import { useAuth } from '../../context/AuthContext'
import { API_BASE } from '../../utils/api'
import type { StrategyDefinition, RsiStraddleConfig, TrendingExpiryConfig } from '../../types'

// ─── Defaults ──────────────────────────────────────────────

const rsiDefaults: RsiStraddleConfig = {
  // Instrument & Position
  underlying: 'NIFTY', expiryType: 'weekly', positionType: 'straddle', quantityLots: 1,
  // Strike Selection
  strikeSelectionMode: 'ATM', strikeOffset: 0, targetDelta: 0.50,
  // Timing
  entryTime: '09:30', noReentryAfter: '14:30', noReentryAfterStrict: '14:30', squareOffTime: '15:20',
  // Risk Management
  slPercent: 0.20, exitPercent: 0.40, minPremiumSl: 10, premiumFloor: 50,
  maxReentryCount: 2, marginPerLot: 150000,
  // VIX RSI
  reentryRsiThresholdHigh: 60, reentryRsiCooldown: 55, reentryRsiPeriod: 14,
  reentryRsiTimeframe: '3min', candleTimeframe: '10min',
  // Order & Advanced
  orderType: 'MARKET', limitBuffer: 2, eventDaySkip: true,
  executionMode: 'scheduled' as const,
}

const trendDefaults: TrendingExpiryConfig = {
  // Instrument & Position
  underlying: 'NIFTY', expiryType: 'weekly', positionType: 'straddle', quantityLots: 1,
  // Strike Selection
  strikeSelectionMode: 'ATM', strikeOffset: 0, targetDelta: 0.50, extraLegStrikeOffset: 1,
  // Timing
  entryStartTime: '09:30', entryEndTime: '14:30', entryIntervalMinutes: 30, squareOffTime: '15:20',
  // Risk Management
  callSLPercent: 0.80, putSLPercent: 0.70, profitTargetPercent: 0.90,
  extraLegSLPercent: 0.50, extraLegTargetPercent: 0.90, extraLegOncePerSide: true,
  extraLegSameQty: true, extraLegQuantityLots: 1,
  perStraddleLossPercent: 0.02, totalStrategyLossPercent: 0.03, marginPerLot: 150000,
  // Order & Advanced
  orderType: 'MARKET', limitBuffer: 2, eventDaySkip: true,
  executionMode: 'scheduled' as const,
}

const mockStrategies: StrategyDefinition[] = [
  {
    id: 's1', name: 'RSI Straddle', type: 'rsi_straddle',
    description: 'RSI-guided ATM straddle with 20% SL trail, 40% target, and VIX RSI re-entry', version: 1,
    params: { ...rsiDefaults }, status: 'active', createdBy: 'system', createdAt: '2026-05-01', updatedAt: '2026-05-15',
  },
  {
    id: 's3', name: 'Trending Expiry', type: 'trending_expiry',
    description: '30-min interval ATM straddle on expiry day with adjustment legs and circuit breakers', version: 1,
    params: { ...trendDefaults }, status: 'active', createdBy: 'system', createdAt: '2026-05-15', updatedAt: '2026-05-15',
  },
]

/** Fields that traders can override from their login */
const TRADER_EDITABLE_FIELDS = new Set([
  'quantityLots', 'targetDelta', 'strikeOffset', 'strikeSelectionMode',
  'maxReentryCount', 'reentryEnabled', 'entryTime', 'entryStartTime',
  'noReentryAfter', 'entryEndTime', 'executionMode',
])

// ─── Field descriptions ────────────────────────────────────

const fieldDescriptions: Record<string, string> = {
  underlying: 'Index or underlying instrument to trade options on',
  expiryType: 'Which expiry cycle to use for strike selection',
  positionType: 'Straddle = same strike CE+PE; Strangle = different CE/PE strikes',
  quantityLots: 'Number of lots per leg (1 lot = exchange-defined lot size)',
  strikeSelectionMode: 'How to select the strike — ATM, ITM, OTM, or by Delta',
  strikeOffset: 'Strikes offset from ATM (e.g. +1 = 1 strike OTM, -1 = 1 strike ITM)',
  targetDelta: 'Target option delta when using DELTA selection mode (0.30 = 30Δ)',
  entryTime: 'Market time (IST) to initiate the straddle write',
  entryStartTime: 'First straddle entry time (IST)',
  entryEndTime: 'Last straddle entry time (IST)',
  entryIntervalMinutes: 'Minutes between successive straddle entries',
  squareOffTime: 'Force exit all remaining positions at this time (IST)',
  noReentryAfter: 'Soft cutoff — no new re-entries after this time (IST)',
  noReentryAfterStrict: 'Hard cutoff — force exit all positions after this time (IST)',
  slPercent: 'Stop loss as a fraction of premium (e.g. 0.20 = 20%)',
  exitPercent: 'Target profit as a fraction of premium decay (e.g. 0.40 = 40%)',
  minPremiumSl: 'Absolute minimum SL in ₹ when premium drops below floor',
  premiumFloor: 'Premium level below which absolute SL kicks in instead of %',
  maxReentryCount: 'Maximum number of re-entries after SL hits',
  marginPerLot: 'Approximate margin required per lot in ₹ (for circuit breaker calculations)',
  callSLPercent: 'Stop loss for Call leg as fraction (e.g. 0.80 = 80% move triggers SL)',
  putSLPercent: 'Stop loss for Put leg as fraction (e.g. 0.70 = 70% move triggers SL)',
  profitTargetPercent: 'Profit target for original legs — 0.90 = exit at 90% premium decay',
  extraLegSLPercent: 'SL for the adjusted extra leg (e.g. 0.50 = 50% move)',
  extraLegTargetPercent: 'Target for the adjusted extra leg (e.g. 0.90 = 90% decay)',
  extraLegOncePerSide: 'Allow only one extra leg adjustment per side (CE/PE) per set',
  extraLegStrikeOffset: 'OTM strike offset for adjustment legs (e.g. 1 = 1 strike OTM)',
  perStraddleLossPercent: 'Per-set circuit breaker — max loss as % of set margin',
  totalStrategyLossPercent: 'Strategy-wide daily circuit breaker — max loss as % of total margin',
  reentryRsiThresholdHigh: 'VIX RSI must be below this for instant re-entry',
  reentryRsiCooldown: 'VIX RSI cooldown threshold — wait until RSI drops below this',
  reentryRsiPeriod: 'RSI lookback period in candles',
  reentryRsiTimeframe: 'Candle timeframe for VIX RSI calculation',
  candleTimeframe: 'Candle timeframe used for trailing SL updates',
  orderType: 'Order execution type — MARKET for instant fills, LIMIT for price control',
  limitBuffer: 'Buffer points from LTP for limit orders (ignored for MARKET)',
  eventDaySkip: 'Skip strategy execution on event days (RBI policy, elections, etc.)',
}

// ─── Validation ────────────────────────────────────────────

interface FieldRule { min?: number; max?: number; step?: number; integer?: boolean }

const commonValidation: Record<string, FieldRule> = {
  quantityLots: { min: 1, max: 999, step: 1, integer: true },
  strikeOffset: { min: -10, max: 10, step: 1, integer: true },
  targetDelta: { min: 0.01, max: 1.0, step: 0.05 },
  marginPerLot: { min: 10000, max: 5000000, step: 10000, integer: true },
  limitBuffer: { min: 0, max: 50, step: 1 },
}

const rsiValidation: Record<string, FieldRule> = {
  ...commonValidation,
  slPercent: { min: 0.01, max: 1.0, step: 0.01 },
  exitPercent: { min: 0.01, max: 1.0, step: 0.01 },
  minPremiumSl: { min: 1, max: 100, step: 1, integer: true },
  premiumFloor: { min: 10, max: 500, step: 5, integer: true },
  maxReentryCount: { min: 0, max: 10, step: 1, integer: true },
  reentryRsiThresholdHigh: { min: 1, max: 100, step: 1 },
  reentryRsiCooldown: { min: 1, max: 100, step: 1 },
  reentryRsiPeriod: { min: 2, max: 50, step: 1, integer: true },
}

const trendValidation: Record<string, FieldRule> = {
  ...commonValidation,
  entryIntervalMinutes: { min: 5, max: 120, step: 5, integer: true },
  callSLPercent: { min: 0.01, max: 2.0, step: 0.01 },
  putSLPercent: { min: 0.01, max: 2.0, step: 0.01 },
  profitTargetPercent: { min: 0.01, max: 1.0, step: 0.01 },
  extraLegSLPercent: { min: 0.01, max: 2.0, step: 0.01 },
  extraLegTargetPercent: { min: 0.01, max: 1.0, step: 0.01 },
  perStraddleLossPercent: { min: 0.001, max: 0.1, step: 0.005 },
  totalStrategyLossPercent: { min: 0.001, max: 0.1, step: 0.005 },
  extraLegStrikeOffset: { min: 0, max: 10, step: 1, integer: true },
}

const percentageFields = new Set([
  'slPercent', 'exitPercent', 'putSLPercent', 'callSLPercent',
  'extraLegSLPercent', 'extraLegTargetPercent', 'profitTargetPercent',
  'perStraddleLossPercent', 'totalStrategyLossPercent', 'targetDelta',
])

function validateField(_key: string, value: number, rule?: FieldRule): string | null {
  if (!rule) return null
  if (isNaN(value)) return 'Must be a valid number'
  if (rule.integer && !Number.isInteger(value)) return 'Must be a whole number'
  if (rule.min !== undefined && value < rule.min) return `Min: ${rule.min}`
  if (rule.max !== undefined && value > rule.max) return `Max: ${rule.max}`
  return null
}

// ─── UI Helpers ────────────────────────────────────────────

function FieldHint({ text }: { text?: string }) {
  if (!text) return null
  return <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: 400, marginTop: '0.125rem' }}>{text}</span>
}

function PercentDisplay({ value }: { value: number }) {
  return (
    <span style={{ fontSize: '0.6875rem', color: 'var(--accent)', fontFamily: "'JetBrains Mono', monospace", marginLeft: '0.375rem' }}>
      = {(value * 100).toFixed(value < 0.1 ? 1 : 0)}%
    </span>
  )
}

function ValidationError({ message }: { message: string | null }) {
  if (!message) return null
  return <span style={{ fontSize: '0.6875rem', color: 'var(--red)', fontWeight: 500, marginTop: '0.125rem' }}>⚠ {message}</span>
}

function SectionHeader({ icon, title, infoTerm }: { icon: string; title: string; infoTerm?: string }) {
  return (
    <div style={{
      gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '0.5rem',
      padding: '0.5rem 0', marginTop: '0.5rem', borderTop: '1px solid var(--border)',
      fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em',
      color: 'var(--accent)',
    }}>
      <span>{icon}</span> {title}{infoTerm && <InfoTip term={infoTerm} />}
    </div>
  )
}

// ─── Main Component ────────────────────────────────────────

export function AdminStrategies() {
  const [strategies, setStrategies] = useState(mockStrategies)
  const [modal, setModal] = useState<{ mode: 'edit'; data: StrategyDefinition } | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const { addToast } = useToast()
  const { session } = useAuth()

  // Fetch strategies from API on mount
  useEffect(() => {
    const fetchStrategies = async () => {
      try {
        const token = session?.token
        if (!token) return
        const res = await fetch(`${API_BASE}/api/strategies`, {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (res.ok) {
          const data = await res.json() as StrategyDefinition[]
          if (data && Array.isArray(data) && data.length > 0) {
            setStrategies(data)
          }
        }
      } catch {
        // Fallback to hardcoded defaults if API unavailable
      }
    }
    fetchStrategies()
  }, [session?.token])

  const handleSave = async (def: StrategyDefinition) => {
    // Save to API
    try {
      const token = session?.token
      if (token) {
        const res = await fetch(`${API_BASE}/api/strategies/${def.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ params: def.params }),
        })
        if (res.ok) {
          addToast(`Strategy "${def.name}" saved to server`, 'success')
        } else {
          addToast(`Failed to save to server — saved locally only`, 'warning')
        }
      }
    } catch {
      addToast(`Server unreachable — saved locally only`, 'warning')
    }
    // Update local state
    setStrategies((p) => p.map((s) => s.id === def.id ? def : s))
    setModal(null)
  }

  return (
    <div>
      <div className="page-head">
        <h1>Strategy Configuration <InfoTip term="strategyConfig" placement="bottom" /></h1>
        <p className="page-sub">Manage strategy parameters for all traders. Changes only apply to new executions.</p>
      </div>

      <div className="strat-list">
        {strategies.map((s) => {
          const p = s.params as any
          const isExp = expanded === s.id
          const isRsi = s.type === 'rsi_straddle'
          return (
            <div key={s.id} className={`strat-row ${s.status}`}>
              {/* ── Main Row ── */}
              <div className="strat-row-main">
                <span className={`strat-status-dot ${s.status}`} />

                <div className="strat-identity">
                  <div className="strat-name">{s.name}</div>
                  <div className="strat-meta">
                    <span className={`strat-type-pill ${isRsi ? '' : 'trend'}`}>
                      {isRsi ? 'RSI Straddle' : 'Trending Expiry'}
                    </span>
                    <span className="strat-version">v{s.version}</span>
                  </div>
                </div>

                <span className="strat-divider" />

                {/* Inline stat chips */}
                <div className="strat-chips">
                  <div className="strat-chip"><span className="strat-chip-k">Index</span><span className="strat-chip-v">{p.underlying}</span></div>
                  <div className="strat-chip"><span className="strat-chip-k">Expiry</span><span className="strat-chip-v">{p.expiryType}</span></div>
                  <div className="strat-chip"><span className="strat-chip-k">Position</span><span className="strat-chip-v">{p.positionType}</span></div>
                  <div className="strat-chip"><span className="strat-chip-k">Lots</span><span className="strat-chip-v">{p.quantityLots} <span style={{ fontSize: '0.625rem', color: 'var(--text-muted)' }}>({getQty(p.quantityLots, p.underlying)} qty)</span></span></div>
                  <div className="strat-chip"><span className="strat-chip-k">Strike</span><span className="strat-chip-v">{p.strikeSelectionMode}</span></div>
                  {isRsi ? (<>
                    <div className="strat-chip"><span className="strat-chip-k">SL</span><span className="strat-chip-v red">{(p.slPercent * 100).toFixed(0)}%</span></div>
                    <div className="strat-chip"><span className="strat-chip-k">Target</span><span className="strat-chip-v green">{(p.exitPercent * 100).toFixed(0)}%</span></div>
                    <div className="strat-chip"><span className="strat-chip-k">Re-entry</span><span className="strat-chip-v">{p.maxReentryCount}</span></div>
                  </>) : (<>
                    <div className="strat-chip"><span className="strat-chip-k">Call SL</span><span className="strat-chip-v red">{(p.callSLPercent * 100).toFixed(0)}%</span></div>
                    <div className="strat-chip"><span className="strat-chip-k">Put SL</span><span className="strat-chip-v red">{(p.putSLPercent * 100).toFixed(0)}%</span></div>
                    <div className="strat-chip"><span className="strat-chip-k">Interval</span><span className="strat-chip-v">{p.entryIntervalMinutes}m</span></div>
                  </>)}
                  <div className="strat-chip"><span className="strat-chip-k">Order</span><span className="strat-chip-v">{p.orderType}</span></div>
                </div>

                <span className="strat-divider" />

                {/* Actions */}
                <div className="strat-actions">
                  <button className="btn btn-sm btn-ghost" onClick={() => setExpanded(isExp ? null : s.id)}>
                    {isExp ? '▲' : '▼'}
                  </button>
                  <button className="btn btn-sm btn-ghost"
                    onClick={() => setModal({ mode: 'edit', data: { ...s } })}>✏</button>
                  <button className={`btn btn-sm ${s.status === 'active' ? 'btn-ghost' : 'btn-green'}`}
                    onClick={() => setStrategies((prev) => prev.map((x) => x.id === s.id ? { ...x, status: x.status === 'active' ? 'draft' : 'active' } as StrategyDefinition : x))}>
                    {s.status === 'active' ? '⏸' : '▶'}
                  </button>
                </div>
              </div>

              {/* ── Expanded Detail Panel ── */}
              {isExp && (
                <div className="strat-expand">
                  <div className="strat-desc-line">{s.description}</div>
                  <div className="strat-detail-cols">
                    {/* Col 1: Timing */}
                    <div className="strat-detail-col">
                      <div className="strat-detail-title">⏱ Timing</div>
                      {isRsi ? (<>
                        <div className="strat-detail-item"><span className="strat-dk">Entry</span><span className="strat-dv">{p.entryTime}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Square Off</span><span className="strat-dv">{p.squareOffTime}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Soft Cutoff</span><span className="strat-dv">{p.noReentryAfter}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Hard Cutoff</span><span className="strat-dv">{p.noReentryAfterStrict}</span></div>
                      </>) : (<>
                        <div className="strat-detail-item"><span className="strat-dk">Start</span><span className="strat-dv">{p.entryStartTime}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">End</span><span className="strat-dv">{p.entryEndTime}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Interval</span><span className="strat-dv">{p.entryIntervalMinutes}min</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Square Off</span><span className="strat-dv">{p.squareOffTime}</span></div>
                      </>)}
                    </div>
                    {/* Col 2: Risk */}
                    <div className="strat-detail-col">
                      <div className="strat-detail-title">📊 Risk</div>
                      {isRsi ? (<>
                        <div className="strat-detail-item"><span className="strat-dk">SL</span><span className="strat-dv red">{(p.slPercent * 100).toFixed(0)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Target</span><span className="strat-dv green">{(p.exitPercent * 100).toFixed(0)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Min SL</span><span className="strat-dv">₹{p.minPremiumSl}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Prem Floor</span><span className="strat-dv">₹{p.premiumFloor}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Re-entries</span><span className="strat-dv">{p.maxReentryCount}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Margin/Lot</span><span className="strat-dv">₹{(p.marginPerLot / 1000).toFixed(0)}K</span></div>
                      </>) : (<>
                        <div className="strat-detail-item"><span className="strat-dk">Call SL</span><span className="strat-dv red">{(p.callSLPercent * 100).toFixed(0)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Put SL</span><span className="strat-dv red">{(p.putSLPercent * 100).toFixed(0)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Target</span><span className="strat-dv green">{(p.profitTargetPercent * 100).toFixed(0)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Set Limit</span><span className="strat-dv">{(p.perStraddleLossPercent * 100).toFixed(1)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Day Limit</span><span className="strat-dv">{(p.totalStrategyLossPercent * 100).toFixed(1)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Margin/Lot</span><span className="strat-dv">₹{(p.marginPerLot / 1000).toFixed(0)}K</span></div>
                      </>)}
                    </div>
                    {/* Col 3: VIX / Adjustments */}
                    <div className="strat-detail-col">
                      <div className="strat-detail-title">{isRsi ? '📈 VIX RSI' : '🔄 Adjustments'}</div>
                      {isRsi ? (<>
                        <div className="strat-detail-item"><span className="strat-dk">Threshold</span><span className="strat-dv">{p.reentryRsiThresholdHigh}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Cooldown</span><span className="strat-dv">{p.reentryRsiCooldown}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Period</span><span className="strat-dv">{p.reentryRsiPeriod}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">RSI TF</span><span className="strat-dv">{p.reentryRsiTimeframe}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Candle TF</span><span className="strat-dv">{p.candleTimeframe}</span></div>
                      </>) : (<>
                        <div className="strat-detail-item"><span className="strat-dk">Extra SL</span><span className="strat-dv">{(p.extraLegSLPercent * 100).toFixed(0)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Extra Target</span><span className="strat-dv">{(p.extraLegTargetPercent * 100).toFixed(0)}%</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Strike Offset</span><span className="strat-dv">{p.extraLegStrikeOffset}</span></div>
                        <div className="strat-detail-item"><span className="strat-dk">Once/Side</span><span className="strat-dv">{p.extraLegOncePerSide ? '✓' : '✗'}</span></div>
                      </>)}
                    </div>
                    {/* Col 4: Advanced */}
                    <div className="strat-detail-col">
                      <div className="strat-detail-title">🔧 Advanced</div>
                      <div className="strat-detail-item"><span className="strat-dk">Order</span><span className="strat-dv">{p.orderType}</span></div>
                      {p.orderType === 'LIMIT' && <div className="strat-detail-item"><span className="strat-dk">Buffer</span><span className="strat-dv">{p.limitBuffer}pts</span></div>}
                      <div className="strat-detail-item"><span className="strat-dk">Event Skip</span><span className="strat-dv">{p.eventDaySkip ? '✓ Yes' : '✗ No'}</span></div>
                      <div className="strat-detail-item"><span className="strat-dk">Updated</span><span className="strat-dv">{s.updatedAt.slice(0, 10)}</span></div>
                      <div className="strat-detail-item"><span className="strat-dk">Created</span><span className="strat-dv">{s.createdAt.slice(0, 10)}</span></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {modal && <StrategyFormModal mode={modal.mode} data={modal.data} onSave={handleSave} onClose={() => setModal(null)} />}
    </div>
  )
}

// ─── Form Modal ────────────────────────────────────────────

function StrategyFormModal({ mode, data, onSave, onClose }: {
  mode: 'edit'; data: StrategyDefinition; onSave: (d: StrategyDefinition) => void; onClose: () => void
}) {
  const [form, setForm] = useState(data)
  const [errors, setErrors] = useState<Record<string, string | null>>({})
  const [qty, setQty] = useState(getQty((data.params as any).quantityLots ?? 1, (data.params as any).underlying ?? 'NIFTY'))

  const setType = (type: 'rsi_straddle' | 'trending_expiry') => {
    const newParams = type === 'trending_expiry' ? { ...trendDefaults } : { ...rsiDefaults }
    setForm((p) => ({ ...p, type, params: newParams as any }))
    setQty(getQty(newParams.quantityLots, newParams.underlying))
    setErrors({})
  }

  const updateStr = (key: string, val: string) => {
    setForm((p) => {
      const next = { ...p, params: { ...p.params, [key]: val } as any }
      // When underlying changes, recalculate qty from current lots
      if (key === 'underlying') {
        setQty(getQty((next.params as any).quantityLots ?? 1, val))
      }
      return next
    })
  }

  const updateNum = useCallback((key: string, raw: string) => {
    const val = parseFloat(raw)
    const rules = form.type === 'rsi_straddle' ? rsiValidation : trendValidation
    const rule = rules[key]
    if (!isNaN(val)) {
      setForm((p) => {
        const next = { ...p, params: { ...p.params, [key]: val } as any }
        // When lots change via the Lots input, sync qty
        if (key === 'quantityLots') {
          setQty(getQty(val, (next.params as any).underlying))
        }
        return next
      })
      const err = validateField(key, val, rule)
      setErrors((prev) => ({ ...prev, [key]: err }))
    }
  }, [form.type])

  const updateBool = (key: string, val: boolean) =>
    setForm((p) => ({ ...p, params: { ...p.params, [key]: val } as any }))

  const resetToDefaults = () => {
    const defaults = form.type === 'trending_expiry' ? { ...trendDefaults } : { ...rsiDefaults }
    setForm((p) => ({ ...p, params: defaults as any }))
    setErrors({})
  }

  const handleSubmit = () => {
    const rules = form.type === 'rsi_straddle' ? rsiValidation : trendValidation
    const newErrors: Record<string, string | null> = {}
    let hasError = false
    for (const [key, rule] of Object.entries(rules)) {
      const val = (form.params as any)[key]
      if (typeof val === 'number') {
        const err = validateField(key, val, rule)
        newErrors[key] = err
        if (err) hasError = true
      }
    }
    setErrors(newErrors)
    if (hasError) return
    onSave({ ...form, id: form.id || crypto.randomUUID(), version: form.version })
  }

  const validation = form.type === 'rsi_straddle' ? rsiValidation : trendValidation
  const p = form.params as any

  // Reusable field renderers
  const numField = (key: string, label: string, opts?: { showPercent?: boolean; infoTerm?: string }) => (
    <div className="cfg-field">
      <label className="cfg-label">
        {label}{opts?.infoTerm && <InfoTip term={opts.infoTerm} />}
        {opts?.showPercent && percentageFields.has(key) && typeof p[key] === 'number' && <PercentDisplay value={p[key]} />}
      </label>
      <input type="number" className="cfg-input" step={validation[key]?.step} min={validation[key]?.min} max={validation[key]?.max}
        value={p[key]} onChange={(e) => updateNum(key, e.target.value)} />
      <FieldHint text={fieldDescriptions[key]} />
      <ValidationError message={errors[key] || null} />
    </div>
  )

  const timeField = (key: string, label: string, opts?: { infoTerm?: string }) => (
    <div className="cfg-field">
      <label className="cfg-label">{label}{opts?.infoTerm && <InfoTip term={opts.infoTerm} />}</label>
      <div className="cfg-time-wrap">
        <input type="time" className="cfg-input" value={p[key]} onChange={(e) => updateStr(key, e.target.value)} />
        <span className="cfg-time-icon">🕐</span>
      </div>
      <FieldHint text={fieldDescriptions[key]} />
    </div>
  )

  const selectField = (key: string, label: string, options: { value: string; label: string }[], opts?: { infoTerm?: string }) => (
    <div className="cfg-field">
      <label className="cfg-label">{label}{opts?.infoTerm && <InfoTip term={opts.infoTerm} />}</label>
      <select className="cfg-input" value={p[key]} onChange={(e) => updateStr(key, e.target.value)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <FieldHint text={fieldDescriptions[key]} />
    </div>
  )

  const toggleField = (key: string, label: string, opts?: { infoTerm?: string }) => (
    <div className="cfg-field">
      <label className="cfg-label">{label}{opts?.infoTerm && <InfoTip term={opts.infoTerm} />}</label>
      <label className="cfg-toggle">
        <input type="checkbox" checked={p[key]} onChange={(e) => updateBool(key, e.target.checked)} />
        <span className="cfg-toggle-track"><span className="cfg-toggle-knob" /></span>
        {p[key] ? 'Yes' : 'No'}
      </label>
      <FieldHint text={fieldDescriptions[key]} />
    </div>
  )

  const underlyingOptions = [
    { value: 'NIFTY', label: 'NIFTY 50' },
    { value: 'BANKNIFTY', label: 'BANK NIFTY' },
    { value: 'FINNIFTY', label: 'FIN NIFTY' },
    { value: 'MIDCPNIFTY', label: 'MIDCAP NIFTY' },
    { value: 'SENSEX', label: 'SENSEX' },
    { value: 'BANKEX', label: 'BANKEX' },
  ]
  const expiryOptions = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ]
  const positionOptions = form.type === 'rsi_straddle'
    ? [{ value: 'straddle', label: 'Straddle (Same Strike CE+PE)' }]
    : [
      { value: 'straddle', label: 'Straddle (Same Strike CE+PE)' },
      { value: 'strangle', label: 'Strangle (Different CE/PE Strikes)' },
    ]
  const strikeModeOptions = [
    { value: 'ATM', label: 'ATM — At The Money' },
  ]
  const orderTypeOptions = [
    { value: 'MARKET', label: 'MARKET — Instant Fill' },
    { value: 'LIMIT', label: 'LIMIT — Price Control' },
  ]

  return (
    <div className="strat-sidebar-overlay" onClick={onClose}>
      <div className="strat-sidebar-panel" onClick={(e) => e.stopPropagation()}>
        <div className="strat-sidebar-header">
          <h3>✏ Edit Strategy Defaults</h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="strat-sidebar-body">
          {/* ⚠️ Warning banner */}
          <div style={{
            padding: '0.625rem 0.75rem', marginBottom: '0.75rem',
            background: 'rgba(250,204,21,0.1)', borderRadius: '6px',
            border: '1px solid rgba(250,204,21,0.3)',
            fontSize: '0.75rem', color: '#fbbf24', fontWeight: 500,
            display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
          }}>
            <span style={{ fontSize: '1rem' }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Changes affect new executions only</div>
              <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                Existing trader executions will continue with their original parameters.
                Only new executions created after this save will use these updated defaults.
              </div>
            </div>
          </div>
          {/* Strategy identity */}
          <div className="cfg-grid" style={{ marginBottom: '0.5rem' }}>
            <div className="cfg-field">
              <label className="cfg-label">Strategy Name</label>
              <input className="cfg-input" value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} placeholder="e.g. RSI NIFTY ATM Straddle" />
            </div>
            <div className="cfg-field">
              <label className="cfg-label">Type {form.type === 'rsi_straddle' ? <InfoTip term="rsiStraddle" /> : <InfoTip term="trending" />}</label>
              <select className="cfg-input" value={form.type}
                onChange={(e) => setType(e.target.value as any)}>
                <option value="rsi_straddle">RSI Straddle</option>
                <option value="trending_expiry">Trending Expiry</option>
              </select>
            </div>
            <div className="cfg-field" style={{ gridColumn: '1 / -1' }}>
              <label className="cfg-label">Description</label>
              <input className="cfg-input" value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="Brief description..." />
            </div>
          </div>

          {/* ═══ SECTION 1: Instrument & Position ═══ */}
          <div className="cfg-grid">
            <SectionHeader icon="📋" title="Instrument & Position" />
            {selectField('underlying', 'Underlying Index', underlyingOptions, { infoTerm: 'underlying' })}
            {selectField('expiryType', 'Expiry Type', expiryOptions, { infoTerm: 'expiry' })}
            {selectField('positionType', 'Position Type', positionOptions, { infoTerm: 'positionType' })}
            <div className="cfg-field">
              <label className="cfg-label">Quantity <InfoTip term="lots" /></label>
              <input type="number" className="cfg-input" value={qty} min={getLotSize(p.underlying)} step={getLotSize(p.underlying)}
                onChange={(e) => {
                  const newQty = parseInt(e.target.value) || getLotSize(p.underlying)
                  const lotSize = getLotSize(p.underlying)
                  const newLots = Math.max(1, Math.round(newQty / lotSize))
                  const snappedQty = newLots * lotSize
                  setQty(snappedQty)
                  updateNum('quantityLots', String(newLots))
                }} />
              <FieldHint text={`Total quantity (multiples of lot size ${getLotSize(p.underlying)})`} />
            </div>
            {numField('quantityLots', 'Lots per Leg', { infoTerm: 'lots' })}
            <div style={{ marginTop: '-0.5rem', marginBottom: '0.5rem', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
              = {getQty(p.quantityLots, p.underlying)} qty <span style={{ opacity: 0.6 }}>(lot size: {getLotSize(p.underlying)})</span>
            </div>
          </div>

          {/* ═══ SECTION 2: Strike Selection ═══ */}
          <div className="cfg-grid">
            <SectionHeader icon="🎯" title="Strike Selection" />
            {selectField('strikeSelectionMode', 'Strike Mode', strikeModeOptions)}
            {p.strikeSelectionMode !== 'DELTA' && numField('strikeOffset', 'Strike Offset')}
            {p.strikeSelectionMode === 'DELTA' && numField('targetDelta', 'Target Delta', { showPercent: true })}
            {form.type === 'trending_expiry' && numField('extraLegStrikeOffset', 'Extra Leg Strike Offset')}
          </div>

          {/* ═══ SECTION 3: Timing ═══ */}
          <div className="cfg-grid">
            <SectionHeader icon="⏱" title="Timing" />
            {form.type === 'rsi_straddle' ? (<>
              {timeField('entryTime', 'Entry Time')}
              {timeField('squareOffTime', 'Square-off Time')}
              {timeField('noReentryAfter', 'Soft Cutoff (No Re-entry)')}
              {timeField('noReentryAfterStrict', 'Hard Cutoff (Force Exit)')}
            </>) : (<>
              {timeField('entryStartTime', 'Entry Start')}
              {timeField('entryEndTime', 'Entry End')}
              {numField('entryIntervalMinutes', 'Interval (min)')}
              {timeField('squareOffTime', 'Square-off Time')}
            </>)}

          </div>

          {/* ═══ SECTION 4: Risk Management ═══ */}
          <div className="cfg-grid">
            <SectionHeader icon="📊" title="Risk Management" />
            {form.type === 'rsi_straddle' ? (<>
              {numField('slPercent', 'SL %', { showPercent: true, infoTerm: 'slPrice' })}
              {numField('exitPercent', 'Exit Target %', { showPercent: true, infoTerm: 'targetPrice' })}
              {numField('minPremiumSl', 'Min SL (₹)', { infoTerm: 'slPrice' })}
              {numField('premiumFloor', 'Premium Floor (₹)')}
              {numField('maxReentryCount', 'Max Re-entries')}
              {numField('marginPerLot', 'Margin per Lot (₹)', { infoTerm: 'margin' })}
            </>) : (<>
              {numField('callSLPercent', 'Call SL %', { showPercent: true, infoTerm: 'slPrice' })}
              {numField('putSLPercent', 'Put SL %', { showPercent: true, infoTerm: 'slPrice' })}
              {numField('profitTargetPercent', 'Profit Target %', { showPercent: true, infoTerm: 'targetPrice' })}
              {numField('extraLegSLPercent', 'Extra Leg SL %', { showPercent: true })}
              {numField('extraLegTargetPercent', 'Extra Leg Target %', { showPercent: true })}
              {toggleField('extraLegOncePerSide', 'Extra Leg Once/Side')}
              {toggleField('extraLegSameQty', 'Adj Leg Same Qty as Entry')}
              {!(p as any).extraLegSameQty && <>
                {numField('extraLegQuantityLots', 'Adj Leg Lots')}
                <div style={{ marginTop: '-0.5rem', marginBottom: '0.5rem', fontSize: '0.6875rem', color: 'var(--text-muted)' }}>
                  = {getQty((p as any).extraLegQuantityLots || 1, p.underlying)} qty
                </div>
              </>}
              {numField('perStraddleLossPercent', 'Per Set Loss Limit %', { showPercent: true })}
              {numField('totalStrategyLossPercent', 'Total Strategy Loss Limit %', { showPercent: true })}
              {numField('marginPerLot', 'Margin per Lot (₹)', { infoTerm: 'margin' })}
            </>)}
          </div>

          {/* ═══ SECTION 5: Advanced ═══ */}
          <div className="cfg-grid">
            <SectionHeader icon="🔧" title="Advanced" />
            {form.type === 'rsi_straddle' && (<>
              {numField('reentryRsiThresholdHigh', 'VIX RSI Threshold', { infoTerm: 'vixRsi' })}
              {numField('reentryRsiCooldown', 'VIX RSI Cooldown')}
              {numField('reentryRsiPeriod', 'VIX RSI Period')}
              {selectField('reentryRsiTimeframe', 'VIX RSI Timeframe', [
                { value: '1min', label: '1 min' }, { value: '3min', label: '3 min' },
                { value: '5min', label: '5 min' }, { value: '10min', label: '10 min' },
              ])}
              {selectField('candleTimeframe', 'SL Trail Timeframe', [
                { value: '5min', label: '5 min' }, { value: '10min', label: '10 min' }, { value: '15min', label: '15 min' },
              ])}
            </>)}
            {selectField('orderType', 'Order Type', orderTypeOptions)}
            {p.orderType === 'LIMIT' && numField('limitBuffer', 'Limit Buffer (pts)')}
            {toggleField('eventDaySkip', 'Skip Event Days')}
          </div>


        </div>
        <div className="strat-sidebar-footer">
          <button className="btn btn-ghost" onClick={resetToDefaults} style={{ fontSize: '0.75rem' }}>
            ↺ Reset to Defaults
          </button>
          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              {mode === 'edit' ? 'Save Defaults' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
